// src/encounter/lib/to-flag-request.ts
// Build the POST /api/Patients/CreateFlag body. Flags persist by WHOLE-LIST REPLACE — the
// request must carry every current flag plus the delta, or the backend drops the omitted
// ones. add = append a new flag (or un-delete one whose category code already exists);
// delete = soft-delete (isDeleted:true) the matching identifier. No edit (flags are a
// code+category tag). Grounded: patient_flag_popup.dart:95-118 + patient_flags_model.dart.
import type { PatientFlag } from '@/types/patient-summary-api';
import type { CreateFlagItem, FlagCategory } from '@/types/flags-api';

export interface FlagRequestContext {
  patientId: string;
  userId?: string;
  nowIso: string;
}

export type FlagAction =
  | { type: 'add'; category: FlagCategory }
  | { type: 'delete'; identifier: string };

/** Thrown when a retained flag carries no resolvable numeric code, so the whole-list-replace
 *  body cannot be built truthfully. LOUD ON PURPOSE (PROD-1989): this used to serialize as
 *  `code: null`, which the backend rejects with
 *    Error converting value {null} to type 'System.Int32'. Path '[0].flagResponse.code'
 *  Silently coercing or dropping the entry is worse than failing: the request REPLACES the whole
 *  list, so a dropped entry deletes a live safety flag (e.g. "No Blood Products") server-side. */
export class FlagCodeMissingError extends Error {
  // Declared explicitly, not a constructor parameter property: this repo compiles with
  // `erasableSyntaxOnly`, which rejects `constructor(readonly x)`.
  readonly flag: PatientFlag;
  constructor(flag: PatientFlag) {
    super(
      'Cannot build the flag list-replace body: an existing flag has no numeric flagResponse.code ' +
        `(identifier=${flag.identifier ?? 'unknown'}, display=${flag.flagResponse?.display ?? 'unknown'}). ` +
        'Build the body from the RAW PatientFlag[] (GetPatientSummary), not from the rendered IpsSection.',
    );
    this.flag = flag;
    this.name = 'FlagCodeMissingError';
  }
}

/** The numeric code, or throw. The wire type is `System.Int32` and every flag has one — an absent
 *  code means the caller passed a lossy projection, not that the flag is codeless. */
function requireFlagCode(f: PatientFlag): number {
  const raw = f.flagResponse?.code;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (n == null || !Number.isFinite(n)) throw new FlagCodeMissingError(f);
  return n;
}

/** Echo a RETAINED row back intact, in Flutter's exact serialisation.
 *
 *  Flutter never rebuilds these — the popup mutates the objects it read and re-serialises them via
 *  `PatientFlags.toJson` (`patient_flags_model.dart:72-88`), which emits all thirteen keys, with
 *  `dataAbsentReason` defaulting to `""` and `performerDetails` to `{}` rather than null.
 *
 *  React previously emitted five keys and hardcoded `subject: undefined`. Live on dev 2026-08-20
 *  that returned HTTP 500 ("An unexpected error occurred") even with correct codes and a deduped
 *  list — the endpoint wants the whole row back. GetPatientSummary supplies every one of these
 *  fields, so retention is a pass-through, not a fabrication: nothing here invents a value. */
function existingToItem(f: PatientFlag, isDeleted: boolean): CreateFlagItem {
  return {
    identifier: f.identifier ?? null,
    subject: f.subject ?? null,
    encounter: f.encounter ?? null,
    author: f.author ?? null,
    dataAbsentReason: f.dataAbsentReason ?? '',
    startDate: f.startDate ?? null,
    endDate: f.endDate ?? null,
    isDeleted,
    categoryName: f.categoryName ?? f.flagResponse?.categoryName ?? null,
    flagResponse: {
      code: requireFlagCode(f),
      display: f.flagResponse?.display ?? null,
      categoryName: f.flagResponse?.categoryName ?? null,
    },
    createdDate: f.createdDate ?? null,
    createdBy: f.createdBy ?? null,
    performerDetails: f.performerDetails ?? {},
  };
}

/** Collapse the retained list to ONE row per flag code.
 *
 *  GetPatientSummary returns an AGGREGATE that repeats each flag many times — measured live on dev
 *  2026-08-20: a patient showing 13 flags carried **6,308 rows**. The read side has always known
 *  this (`to-ips-sections.ts` dedupes by code for display, "e.g. flag code 1x6, 9x4"), but the
 *  write side must too: posting the raw list sent 6,309 items and the endpoint answered HTTP 500.
 *
 *  This is Flutter-faithful, not a new behaviour. Flutter dedupes flags ON READ at the bloc
 *  (`patient_chart_bloc.dart:413`, `response: uniqueItems<PatientFlags>(success)`), so
 *  `patient_flag_popup.dart` mutates an already-unique list and posts the unique set back.
 *  `uniqueItems` (`:905-913`) keeps the FIRST occurrence per displayCode — same rule as here.
 *
 *  Consequence, stated rather than hidden: because the request REPLACES the list, sending the
 *  unique set collapses the duplicate rows server-side. That is what Flutter's own writes do.
 *
 *  An ACTIVE row wins over a soft-deleted one for the same code, so deduping can never resurrect a
 *  deletion or bury a live flag; a code present only as deleted rows keeps one, which is what lets
 *  the un-delete branch below match instead of appending a duplicate. A row with NO code is kept
 *  deliberately, so `requireFlagCode` throws on it rather than this quietly dropping it — dropping
 *  would DELETE that flag server-side. */
function dedupeFlagsForWrite(flags: PatientFlag[]): PatientFlag[] {
  const idxByCode = new Map<string, number>();
  const out: PatientFlag[] = [];
  for (const f of flags) {
    const raw = f.flagResponse?.code;
    const code = raw === null || raw === undefined ? '' : String(raw).trim();
    if (code === '') { out.push(f); continue; } // unrepresentable — let the builder fail loudly
    const at = idxByCode.get(code);
    if (at === undefined) {
      idxByCode.set(code, out.length);
      out.push(f);
    } else if (out[at].isDeleted && !f.isDeleted) {
      out[at] = f; // prefer the live row over a soft-deleted duplicate
    }
  }
  return out;
}

export function buildCreateFlagBody(
  current: PatientFlag[] | null | undefined,
  action: FlagAction,
  ctx: FlagRequestContext,
): CreateFlagItem[] {
  const list = dedupeFlagsForWrite(current ?? []);

  if (action.type === 'delete') {
    return list.map((f) => existingToItem(f, f.identifier === action.identifier ? true : Boolean(f.isDeleted)));
  }

  // add: if a flag with this code exists, un-delete it; else append.
  const code = action.category.code;
  const idx = list.findIndex((f) => f.flagResponse?.code != null && Number(f.flagResponse.code) === code);
  if (idx >= 0) {
    return list.map((f, i) => existingToItem(f, i === idx ? false : Boolean(f.isDeleted)));
  }
  const kept = list.map((f) => existingToItem(f, Boolean(f.isDeleted)));
  // Same full key set as a retained row — Flutter builds a PatientFlags and toJson's it, so every
  // key is present even when null (patient_flag_popup.dart:113-115 sets only subject/createdBy).
  kept.push({
    identifier: null,
    subject: ctx.patientId,
    encounter: null,
    author: null,
    dataAbsentReason: '',
    startDate: null,
    endDate: null,
    isDeleted: false,
    categoryName: action.category.categoryName ?? null,
    flagResponse: action.category,
    createdDate: ctx.nowIso,
    createdBy: ctx.userId ?? null,
    performerDetails: {},
  });
  return kept;
}
